// Deterministic command parser. Unambiguous user commands are matched here and
// routed straight to a registry tool — skipping the model entirely for an instant,
// 100%-reliable common path. Anything not matched falls through to the model.

export interface FastIntent {
  tool: string;
  args: Record<string, unknown>;
  confirm?: string; // shown to the user when the tool's result isn't user-facing
}

const VENDORS = new Set([
  'amazon',
  'walmart',
  'target',
  'bestbuy',
  'costco',
  'kroger',
  'ebay',
  'etsy',
  'wayfair',
  'newegg',
  'lowes',
]);

const digits = (s: string): string | null => {
  const m = s.match(/\d+/g);
  return m ? m.join(',') : null;
};

// Pulls a budget cap ("under $50", "below 30", "< 20") out of search keywords.
const extractBudget = (s: string): { keywords: string; maxPrice?: number } => {
  const m = s.match(/\s*(?:under|below|less than|cheaper than|<=?|≤)\s*\$?\s*(\d+(?:\.\d+)?)/);
  return m ? { keywords: s.replace(m[0], '').trim(), maxPrice: Number(m[1]) } : { keywords: s };
};

export function matchFastPath(input: string): FastIntent | null {
  const t = input.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return null;

  // pasted product URL → add it (use the original input to keep the URL intact)
  const url = input.match(/https?:\/\/\S+/);
  if (url) return { tool: 'add_by_url', args: { url: url[0] } };

  // save
  if (/^(save|save (the )?(cart|list)|checkout|done|finish)$/.test(t)) {
    return { tool: 'save_cart', args: {} };
  }
  // clear
  if (/^(clear|empty|reset)( (the )?cart)?$|^start over$/.test(t)) {
    return { tool: 'clear_cart', args: {} };
  }
  // view cart
  if (/^(view|show|see)( my| the)? cart$|^cart$|^what'?s in (my |the )?cart\??$/.test(t)) {
    return { tool: 'view_cart', args: {} };
  }
  // set quantity: "set 1 to 3", "change item 2 to 5", "item 1 qty 4"
  let m =
    t.match(/(?:set|change|make)\s+(?:item\s+)?(\d+)\s+(?:to|=|qty|quantity)\s+(\d+)/) ||
    t.match(/(?:item\s+)?(\d+)\s+(?:qty|quantity)\s+(\d+)/);
  if (m) return { tool: 'set_quantity', args: { number: Number(m[1]), quantity: Number(m[2]) } };

  // remove: "remove 2", "delete 1 and 3"
  m = t.match(/^(?:remove|delete|drop|take out)\s+(?:items?\s+)?([\d,\s]+(?:and[\d,\s]+)*)$/);
  if (m) {
    const nums = digits(m[1]);
    if (nums) return { tool: 'remove_from_cart', args: { numbers: nums } };
  }

  // add cheapest: "cheapest headphones", "add 2 cheapest socks on walmart"
  m = t.match(/^(?:add\s+)?(?:(\d+)\s+)?cheapest\s+(.+?)(?:\s+(?:on|at|from)\s+(\w+))?$/);
  if (m && m[2]) {
    const vendor = m[3] && VENDORS.has(m[3]) ? m[3] : 'amazon';
    return {
      tool: 'add_cheapest',
      args: { vendor, keywords: m[2].trim(), count: m[1] ? Number(m[1]) : 1 },
    };
  }

  // add with quantity: "add 2 of 1", "add 3 of 1 and 4"
  m = t.match(/^add\s+(\d+)\s+of\s+(?:items?\s+)?([\d,\s]+(?:and[\d,\s]+)*)$/);
  if (m) {
    const nums = digits(m[2]);
    if (nums) return { tool: 'add_to_cart', args: { numbers: nums, quantity: Number(m[1]) } };
  }

  // add: "add 1", "add 1 and 3", "add items 2,4"
  m = t.match(/^add\s+(?:items?\s+)?([\d,\s]+(?:and[\d,\s]+)*)$/);
  if (m) {
    const nums = digits(m[1]);
    if (nums) return { tool: 'add_to_cart', args: { numbers: nums } };
  }

  // deals: "deals", "any deals", "walmart deals", "deals at target", "better deals on walmart"
  // Checked before search so "find deals at walmart" isn't read as a product search.
  if (/\b(deals?|coupons?|promos?|promotions?|discounts?|sales?)\b/.test(t)) {
    if (/\b(?:my|the) cart\b/.test(t)) return { tool: 'find_deals', args: { retailer: 'cart' } };
    const vendor = Array.from(VENDORS).find((v) => t.includes(v));
    return { tool: 'find_deals', args: vendor ? { retailer: vendor } : {} };
  }

  // whole-cart price check: "compare my cart", "is my cart cheaper elsewhere".
  if (
    /\b(?:my|the|whole|entire) cart\b/.test(t) &&
    /\b(compare|cheaper|cheapest|price[- ]?check|better price|savings?|elsewhere|somewhere)\b/.test(t)
  ) {
    return { tool: 'compare_cart', args: {} };
  }

  // cheaper elsewhere (single cart item): "is this cheaper elsewhere",
  // "is item 2 cheaper somewhere else", "price check 1".
  if (
    /cheaper (?:elsewhere|somewhere|anywhere)|better price (?:elsewhere|somewhere)|price[- ]?check/.test(
      t,
    )
  ) {
    const n = t.match(/\b(\d+)\b/)?.[1];
    return { tool: 'compare_cart_item', args: n ? { number: Number(n) } : {} };
  }

  // compare: "compare airpods across amazon and walmart", "compare prices for X",
  // "compare 1 on amazon, walmart". Checked before search for the same reason.
  if (/^compare\b/.test(t)) {
    let rest = t
      .replace(/^compare\s+/, '')
      .replace(/^(?:the\s+)?prices?\s+(?:for|of|on)\s+/, '')
      .replace(/^(?:this|it|that)\b/, '')
      .trim();
    let vendorsArg: string | undefined;
    // "between/across/on/at X and Y" — may appear at the start ("compare between
    // walmart and amazon") or after a product ("compare airpods on walmart, target").
    const vm = rest.match(/(?:^|\s)(?:across|between|on|at|from|vs\.?)\s+(.+)$/);
    if (vm) {
      const found = vm[1]
        .split(/[\s,]+|\band\b|vs\.?/)
        .map((s) => s.trim())
        .filter((s) => VENDORS.has(s));
      if (found.length) {
        vendorsArg = Array.from(new Set(found)).join(',');
        rest = rest.slice(0, vm.index).trim();
      }
    }
    const args: Record<string, unknown> = {};
    if (/^\d+$/.test(rest)) args.number = Number(rest);
    else if (rest) args.keywords = rest;
    if (vendorsArg) args.vendors = vendorsArg;
    // Emit even with no explicit product — compare_price resolves it from context.
    return { tool: 'compare_price', args };
  }

  // "create a target cart with 5 items related to going back to school"
  m = t.match(
    /^(?:create|build|make|start)\s+(?:me\s+)?(?:a\s+|an\s+)?(?:(\w+)\s+)?(?:cart|list)\b(.*)$/,
  );
  if (m) {
    const rest = `${m[1] ?? ''} ${m[2] ?? ''}`;
    const named = m[1] && VENDORS.has(m[1]) ? m[1] : undefined;
    const vendor = named ?? (Array.from(VENDORS).find((v) => new RegExp(`\\b${v}\\b`).test(rest)) || '');
    if (vendor) {
      const count = rest.match(/\b(\d+)\s+items?\b/)?.[1];
      const keywords = rest
        .replace(new RegExp(`\\b${vendor}\\b`, 'g'), ' ')
        .replace(/\b\d+\s+items?\b/g, ' ')
        .replace(/\brelated to\b/g, ' ')
        .replace(/\b(with|for|about|of|a|an|the|cart|list|items?)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (keywords) {
        return {
          tool: 'search_products',
          args: { vendor, keywords, ...(count ? { count: Number(count) } : {}) },
        };
      }
    }
  }

  // search: "search amazon for X" | "find X on walmart" | "search for X"
  const found = 'Here’s what I found — tap Add on a card or tell me which number.';
  m = t.match(/^(?:search|find|look up|look for)\s+(\w+)\s+for\s+(.+)$/);
  if (m && VENDORS.has(m[1])) {
    const { keywords, maxPrice } = extractBudget(m[2].trim());
    return {
      tool: 'search_products',
      args: { vendor: m[1], keywords, ...(maxPrice != null ? { max_price: maxPrice } : {}) },
      confirm: found,
    };
  }
  m = t.match(/^(?:search|find|look up|look for)\s+(?:for\s+)?(.+?)\s+(?:on|at|from)\s+(\w+)$/);
  if (m && VENDORS.has(m[2])) {
    const { keywords, maxPrice } = extractBudget(m[1].trim());
    return {
      tool: 'search_products',
      args: { vendor: m[2], keywords, ...(maxPrice != null ? { max_price: maxPrice } : {}) },
      confirm: found,
    };
  }

  return null;
}
